import * as React from 'react';
import {
  IGroup,
  ISelectableEntity,
  ISelectOption,
} from '../../../../data/types';
import { IScoreboardSettings, ISplitViewConfig } from '../types';

type SegmentType = 'none' | 'team' | 'group';

const deriveSplitViewSegmentType = (
  splitView: ISplitViewConfig
): SegmentType => {
  if (!splitView?.enabled) return 'none';
  return splitView.leftColumn.filterType;
};

interface UseSettingsHydrationOptions {
  currentSettings: IScoreboardSettings | null;
  groups: IGroup[];
  teams: ISelectableEntity['entity'][];
}

interface UseSettingsHydrationResult {
  selectedTeam: ISelectOption;
  setSelectedTeam: React.Dispatch<React.SetStateAction<ISelectOption>>;
  selectedGroup: ISelectOption;
  setSelectedGroup: React.Dispatch<React.SetStateAction<ISelectOption>>;
  selectedLeftColumn: ISelectOption;
  setSelectedLeftColumn: React.Dispatch<React.SetStateAction<ISelectOption>>;
  selectedRightColumn: ISelectOption;
  setSelectedRightColumn: React.Dispatch<React.SetStateAction<ISelectOption>>;
  selectedLeftColumnSecondaryFilter: ISelectOption;
  setSelectedLeftColumnSecondaryFilter: React.Dispatch<React.SetStateAction<ISelectOption>>;
  selectedRightColumnSecondaryFilter: ISelectOption;
  setSelectedRightColumnSecondaryFilter: React.Dispatch<React.SetStateAction<ISelectOption>>;
  groupOptions: ISelectableEntity[];
  filteredGroupOptions: ISelectableEntity[];
  filteredGroupOptionsForSplit: ISelectableEntity[];
  filteredGroupOptionsForLeftColumn: ISelectableEntity[];
  filteredGroupOptionsForRightColumn: ISelectableEntity[];
}

export const useSettingsHydration = ({
  currentSettings,
  groups,
  teams,
}: UseSettingsHydrationOptions): UseSettingsHydrationResult => {
  const [selectedTeam, setSelectedTeam] = React.useState<ISelectOption>(null);
  const [selectedGroup, setSelectedGroup] = React.useState<ISelectOption>(null);
  const [selectedLeftColumn, setSelectedLeftColumn] =
    React.useState<ISelectOption>(null);
  const [selectedRightColumn, setSelectedRightColumn] =
    React.useState<ISelectOption>(null);
  const [selectedLeftColumnSecondaryFilter, setSelectedLeftColumnSecondaryFilter] =
    React.useState<ISelectOption>(null);
  const [selectedRightColumnSecondaryFilter, setSelectedRightColumnSecondaryFilter] =
    React.useState<ISelectOption>(null);

  // Refs to track current selected values without triggering re-hydration.
  // These are read as guards to avoid overwriting user selections.
  const selectedTeamRef = React.useRef(selectedTeam);
  selectedTeamRef.current = selectedTeam;
  const selectedGroupRef = React.useRef(selectedGroup);
  selectedGroupRef.current = selectedGroup;
  const selectedLeftColumnRef = React.useRef(selectedLeftColumn);
  selectedLeftColumnRef.current = selectedLeftColumn;
  const selectedRightColumnRef = React.useRef(selectedRightColumn);
  selectedRightColumnRef.current = selectedRightColumn;
  const selectedLeftSecondaryRef = React.useRef(selectedLeftColumnSecondaryFilter);
  selectedLeftSecondaryRef.current = selectedLeftColumnSecondaryFilter;
  const selectedRightSecondaryRef = React.useRef(selectedRightColumnSecondaryFilter);
  selectedRightSecondaryRef.current = selectedRightColumnSecondaryFilter;

  // Hydrate selected team/group from saved settings when teams/groups become available.
  React.useEffect((): void => {
    if (!currentSettings || teams.length === 0) return;

    const currentSegmentType = deriveSplitViewSegmentType(
      currentSettings.splitView
    );

    if (currentSegmentType === 'none') {
      const savedTeamId = currentSettings.filteredTeams?.[0];
      if (savedTeamId && !selectedTeamRef.current) {
        const team = teams.find(
          (t: ISelectableEntity['entity']): boolean => t.id === savedTeamId
        );
        if (team) {
          setSelectedTeam({ label: team.name, value: team.id });
        }
      }

      const savedGroupId = currentSettings.filteredGroups?.[0];
      if (savedGroupId && !selectedGroupRef.current) {
        const group = groups.find(
          (g: IGroup): boolean => g.id === savedGroupId
        );
        if (group) {
          setSelectedGroup({ label: group.name, value: group.id });
        }
      }
    } else if (currentSettings.splitView?.enabled) {
      const leftId = currentSettings.splitView.leftColumn?.filteredIds?.[0];
      const rightId = currentSettings.splitView.rightColumn?.filteredIds?.[0];

      if (currentSegmentType === 'team') {
        if (leftId && !selectedLeftColumnRef.current) {
          const leftTeam = teams.find(
            (t: ISelectableEntity['entity']): boolean => t.id === leftId
          );
          if (leftTeam) {
            setSelectedLeftColumn({
              label: leftTeam.name,
              value: leftTeam.id,
            });
          }
        }
        if (rightId && !selectedRightColumnRef.current) {
          const rightTeam = teams.find(
            (t: ISelectableEntity['entity']): boolean => t.id === rightId
          );
          if (rightTeam) {
            setSelectedRightColumn({
              label: rightTeam.name,
              value: rightTeam.id,
            });
          }
        }
        // Hydrate per-column group (secondary) filters
        const leftSecondaryId = currentSettings.splitView.leftColumn?.secondaryFilterIds?.[0];
        if (leftSecondaryId && !selectedLeftSecondaryRef.current) {
          const group = groups.find((g: IGroup): boolean => g.id === leftSecondaryId);
          if (group) {
            setSelectedLeftColumnSecondaryFilter({ label: group.name, value: group.id });
          }
        }
        const rightSecondaryId = currentSettings.splitView.rightColumn?.secondaryFilterIds?.[0];
        if (rightSecondaryId && !selectedRightSecondaryRef.current) {
          const group = groups.find((g: IGroup): boolean => g.id === rightSecondaryId);
          if (group) {
            setSelectedRightColumnSecondaryFilter({ label: group.name, value: group.id });
          }
        }
      } else if (currentSegmentType === 'group') {
        if (leftId && !selectedLeftColumnRef.current) {
          const leftGroup = groups.find(
            (g: IGroup): boolean => g.id === leftId
          );
          if (leftGroup) {
            setSelectedLeftColumn({
              label: leftGroup.name,
              value: leftGroup.id,
            });
          }
        }
        if (rightId && !selectedRightColumnRef.current) {
          const rightGroup = groups.find(
            (g: IGroup): boolean => g.id === rightId
          );
          if (rightGroup) {
            setSelectedRightColumn({
              label: rightGroup.name,
              value: rightGroup.id,
            });
          }
        }
        // Hydrate per-column team (secondary) filters
        const leftSecondaryId = currentSettings.splitView.leftColumn?.secondaryFilterIds?.[0];
        if (leftSecondaryId && !selectedLeftSecondaryRef.current) {
          const team = teams.find(
            (t: ISelectableEntity['entity']): boolean => t.id === leftSecondaryId
          );
          if (team) {
            setSelectedLeftColumnSecondaryFilter({ label: team.name, value: team.id });
          }
        }
        const rightSecondaryId = currentSettings.splitView.rightColumn?.secondaryFilterIds?.[0];
        if (rightSecondaryId && !selectedRightSecondaryRef.current) {
          const team = teams.find(
            (t: ISelectableEntity['entity']): boolean => t.id === rightSecondaryId
          );
          if (team) {
            setSelectedRightColumnSecondaryFilter({ label: team.name, value: team.id });
          }
        }
      }
    }
  }, [currentSettings, teams, groups]);

  const groupOptions = React.useMemo(
    (): ISelectableEntity[] =>
      groups.map(
        (group: IGroup): ISelectableEntity => ({
          label: group.name,
          value: group.id,
          entity: group,
        })
      ),
    [groups]
  );

  const filteredGroupOptions = React.useMemo((): ISelectableEntity[] => {
    if (!selectedTeam) return [];
    return groups
      .filter(
        (group: IGroup): boolean =>
          group.teams?.includes(selectedTeam.value) ?? false
      )
      .map(
        (group: IGroup): ISelectableEntity => ({
          label: group.name,
          value: group.id,
          entity: group,
        })
      );
  }, [groups, selectedTeam?.value]);

  // For split by team mode: show groups that belong to either selected team
  const filteredGroupOptionsForSplit = React.useMemo((): ISelectableEntity[] => {
    if (!selectedLeftColumn && !selectedRightColumn) return [];
    const teamIds = [selectedLeftColumn?.value, selectedRightColumn?.value].filter(Boolean);
    if (teamIds.length === 0) return [];

    return groups
      .filter(
        (group: IGroup): boolean =>
          group.teams?.some((teamId: string): boolean => teamIds.includes(teamId)) ?? false
      )
      .map(
        (group: IGroup): ISelectableEntity => ({
          label: group.name,
          value: group.id,
          entity: group,
        })
      );
  }, [groups, selectedLeftColumn?.value, selectedRightColumn?.value]);

  // Per-column group options for split by team mode
  const filteredGroupOptionsForLeftColumn = React.useMemo((): ISelectableEntity[] => {
    if (!selectedLeftColumn) return [];
    return groups
      .filter(
        (group: IGroup): boolean =>
          group.teams?.includes(selectedLeftColumn.value) ?? false
      )
      .map(
        (group: IGroup): ISelectableEntity => ({
          label: group.name,
          value: group.id,
          entity: group,
        })
      );
  }, [groups, selectedLeftColumn?.value]);

  const filteredGroupOptionsForRightColumn = React.useMemo((): ISelectableEntity[] => {
    if (!selectedRightColumn) return [];
    return groups
      .filter(
        (group: IGroup): boolean =>
          group.teams?.includes(selectedRightColumn.value) ?? false
      )
      .map(
        (group: IGroup): ISelectableEntity => ({
          label: group.name,
          value: group.id,
          entity: group,
        })
      );
  }, [groups, selectedRightColumn?.value]);

  return {
    selectedTeam,
    setSelectedTeam,
    selectedGroup,
    setSelectedGroup,
    selectedLeftColumn,
    setSelectedLeftColumn,
    selectedRightColumn,
    setSelectedRightColumn,
    selectedLeftColumnSecondaryFilter,
    setSelectedLeftColumnSecondaryFilter,
    selectedRightColumnSecondaryFilter,
    setSelectedRightColumnSecondaryFilter,
    groupOptions,
    filteredGroupOptions,
    filteredGroupOptionsForSplit,
    filteredGroupOptionsForLeftColumn,
    filteredGroupOptionsForRightColumn,
  };
};
